/**
 * Workshop Context
 * Global state management for selected workshop with localStorage persistence
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Workshop, WorkshopContextType } from "../types/workshop";

const STORAGE_KEY = "selected_workshop";

const WorkshopContext = createContext<WorkshopContextType | undefined>(
  undefined
);

interface WorkshopProviderProps {
  children: ReactNode;
  workshops: Workshop[];
}

export const WorkshopProvider: React.FC<WorkshopProviderProps> = ({
  children,
  workshops,
}) => {
  const [selectedWorkshop, setSelectedWorkshopState] =
    useState<Workshop | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load selected workshop from localStorage on mount
  useEffect(() => {
    const loadSavedWorkshop = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved) as Workshop;
          // Verify workshop still exists in user's workshops
          const workshopExists = workshops.find((w) => w.id === parsed.id);
          if (workshopExists) {
            setSelectedWorkshopState(workshopExists);
          } else {
            // Workshop no longer available, clear storage
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error("Error loading workshop from localStorage:", error);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    if (workshops.length > 0) {
      loadSavedWorkshop();
    } else {
      setIsLoading(false);
    }
  }, [workshops]);

  // Auto-select if only one workshop available
  useEffect(() => {
    if (!selectedWorkshop && workshops.length === 1) {
      setSelectedWorkshop(workshops[0]);
    }
  }, [workshops, selectedWorkshop]);

  const setSelectedWorkshop = (workshop: Workshop) => {
    const previousWorkshop = selectedWorkshop;
    setSelectedWorkshopState(workshop);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(workshop));
    } catch (error) {
      console.error("Error saving workshop to localStorage:", error);
    }
  };

  const clearWorkshop = () => {
    setSelectedWorkshopState(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const value: WorkshopContextType = {
    selectedWorkshop,
    workshops,
    setSelectedWorkshop,
    isLoading,
    clearWorkshop,
  };

  return (
    <WorkshopContext.Provider value={value}>
      {children}
    </WorkshopContext.Provider>
  );
};

export const useWorkshop = (): WorkshopContextType => {
  const context = useContext(WorkshopContext);
  if (context === undefined) {
    throw new Error("useWorkshop must be used within a WorkshopProvider");
  }
  return context;
};
